import React from "react";
import snakeToPascal from "../../tools";

// Reusable row component for consistent alignment
export function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="grid grid-cols-[120px_1fr] gap-3 py-1.5 border-b border-slate-100 last:border-b-0">
            <span className="text-xs text-slate-500">{label}</span>
            <span className="text-sm text-slate-900">{children}</span>
        </div>
    );
}

// Badge component for type labels
export function TypeBadge({ children, variant = 'default' }: { children: React.ReactNode; variant?: 'default' | 'primary' }) {
    const styles = variant === 'primary'
        ? 'bg-primary/10 text-primary'
        : 'bg-slate-100 text-slate-700';
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${styles}`}>
            {children}
        </span>
    );
}

// Value change component
export function ValueChange({ oldValue, newValue }: { oldValue: any; newValue: any }) {
    const formatValue = (val: any) => {
        if (val === null || val === undefined || val === '') return 'empty';
        return String(val);
    };

    return (
        <div className="flex items-center gap-3 mt-2">
            <div className="flex-1 p-2 rounded bg-red-50 border border-red-100">
                <span className="text-xs text-red-600 block mb-0.5">Before</span>
                <span className="text-sm font-mono text-red-900">{formatValue(oldValue)}</span>
            </div>
            <span className="material-symbols-outlined text-slate-400 text-[18px]">arrow_forward</span>
            <div className="flex-1 p-2 rounded bg-emerald-50 border border-emerald-100">
                <span className="text-xs text-emerald-600 block mb-0.5">After</span>
                <span className="text-sm font-mono text-emerald-900">{formatValue(newValue)}</span>
            </div>
        </div>
    );
}

export default function BaseDetails({ children, row }) {
    const hasBaseInfo = row.model_data_id || row.model_data_type || row.model_data_name;
    const hasChildren = children !== null && children !== undefined;

    // If no base info and no children, show minimal info
    if (!hasBaseInfo && !hasChildren) {
        return (
            <div className="text-sm text-slate-500 italic">
                No additional details available
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {/* Main info grid */}
            {hasBaseInfo && (
                <div className="bg-white rounded-lg border border-slate-200 p-3">
                    {row.model_data_id && (
                        <DetailRow label="Object ID">
                            <span className="font-mono">#{row.model_data_id}</span>
                        </DetailRow>
                    )}
                    {row.model_data_type && (
                        <DetailRow label="Object Type">
                            <TypeBadge variant="primary">{snakeToPascal(row.model_data_type)}</TypeBadge>
                        </DetailRow>
                    )}
                    {row.model_data_name && (
                        <DetailRow label="Object Name">
                            <span className="font-medium">{row.model_data_name}</span>
                        </DetailRow>
                    )}
                </div>
            )}

            {/* Additional content from child components */}
            {hasChildren && (
                <div className="bg-white rounded-lg border border-slate-200 p-3">
                    {children}
                </div>
            )}
        </div>
    );
}
